import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Clock, Euro, Loader2, MapPin, Plus, Route, Users } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/auth";
import { logError } from "@/lib/log";

const eur = (v) =>
  v === null || v === undefined || v === ""
    ? "—"
    : `${Number(v).toLocaleString("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} €`;

const CellInput = ({ value, onSave, suffix, testid, width = "w-20" }) => {
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => setDraft(value ?? ""), [value]);
  const commit = () => {
    const raw = String(draft).replace(",", ".").trim();
    if (raw === String(value ?? "")) return;
    if (raw !== "" && !Number.isFinite(Number(raw))) {
      setDraft(value ?? "");
      return;
    }
    onSave(raw === "" ? null : Number(raw));
  };
  return (
    <span className="inline-flex items-center gap-1">
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        inputMode="decimal"
        className={`${width} h-9 border-zinc-300 px-2 text-right text-sm tabular-nums focus-visible:border-zinc-900 focus-visible:ring-0`}
        data-testid={testid}
      />
      {suffix && <span className="text-xs text-zinc-500">{suffix}</span>}
    </span>
  );
};

export default function PricesManager() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newRegion, setNewRegion] = useState({ name: "", desfiemer_code: "" });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/prices");
      setData(res.data);
    } catch (e) {
      logError("No se pudieron cargar los precios", e);
      toast.error("No se pudieron cargar los precios.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveRegion = async (region, patch, key) => {
    setSavingKey(key);
    try {
      await adminApi.put(`/admin/prices/region/${region.region_id}`, patch);
      setData((d) => ({
        ...d,
        regions: d.regions.map((r) =>
          r.region_id === region.region_id
            ? {
                ...r,
                ...("hours_initial" in patch ? { hours_initial: patch.hours_initial } : {}),
                ...("hours_refresher" in patch
                  ? { hours_refresher: patch.hours_refresher }
                  : {}),
                ...("max_students" in patch ? { max_students: patch.max_students } : {}),
                ...("price_initial" in patch
                  ? { initial: { ...r.initial, unit_price: patch.price_initial } }
                  : {}),
                ...("price_refresher" in patch
                  ? { refresher: { ...r.refresher, unit_price: patch.price_refresher } }
                  : {}),
              }
            : r
        ),
      }));
      toast.success("Guardado.");
    } catch (e) {
      logError("No se pudo guardar", e);
      toast.error(e?.response?.data?.detail || "No se pudo guardar.");
      await load();
    } finally {
      setSavingKey("");
    }
  };

  const saveTrip = async (trip, unit_price) => {
    if (!trip.product_id) {
      toast.error("Ese tramo no está en el catálogo de Facturación.");
      return;
    }
    setSavingKey(trip.product_id);
    try {
      await adminApi.put(`/admin/prices/trip/${trip.product_id}`, { unit_price });
      setData((d) => ({
        ...d,
        trips: d.trips.map((t) =>
          t.product_id === trip.product_id ? { ...t, unit_price } : t
        ),
      }));
      toast.success("Precio de desplazamiento actualizado.");
    } catch (e) {
      logError("No se pudo guardar el desplazamiento", e);
      toast.error(e?.response?.data?.detail || "No se pudo guardar.");
      await load();
    } finally {
      setSavingKey("");
    }
  };

  const createRegion = async () => {
    if (!newRegion.name.trim()) {
      toast.error("Pon el nombre de la comunidad.");
      return;
    }
    setCreating(true);
    try {
      await adminApi.post("/regions", {
        name: newRegion.name.trim().toUpperCase(),
        desfiemer_code: newRegion.desfiemer_code.trim().toUpperCase() || null,
      });
      toast.success("Comunidad añadida.");
      setDialogOpen(false);
      setNewRegion({ name: "", desfiemer_code: "" });
      await load();
    } catch (e) {
      logError("No se pudo crear la comunidad", e);
      toast.error(e?.response?.data?.detail || "No se pudo crear la comunidad.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <section data-testid="prices-manager">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-extrabold tracking-tight text-zinc-950">
            <Euro className="h-5 w-5" />
            Precios
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-zinc-600">
            Horas y precio de cada formación por comunidad. Los importes son los
            que la app pone en las facturas: cámbialos cuando quieras y se
            aplican a las facturas nuevas.
          </p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="h-10 rounded-sm bg-zinc-950 px-4 text-sm font-semibold hover:bg-zinc-800"
          data-testid="prices-add-region-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Añadir comunidad
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cargando precios…
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-sm border-2 border-zinc-200 bg-white">
            <table className="w-full min-w-[920px] text-sm">
              <thead className="sticky top-0 z-10 bg-zinc-950 text-white">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wide">
                    Comunidad
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-bold uppercase tracking-wide">
                    <Clock className="mr-1 inline h-3.5 w-3.5" />h inicial
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-bold uppercase tracking-wide">
                    € inicial
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-bold uppercase tracking-wide">
                    <Clock className="mr-1 inline h-3.5 w-3.5" />h reciclaje
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-bold uppercase tracking-wide">
                    € reciclaje
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-bold uppercase tracking-wide">
                    <Users className="mr-1 inline h-3.5 w-3.5" />Máx.
                  </th>
                </tr>
              </thead>
              <tbody>
                {(data?.regions || []).map((r) => (
                  <tr
                    key={r.region_id}
                    className="border-t border-zinc-200 hover:bg-zinc-50"
                    data-testid={`prices-row-${r.region_id}`}
                  >
                    <td className="px-3 py-2">
                      <span className="flex items-center gap-2 font-semibold text-zinc-900">
                        <MapPin className="h-3.5 w-3.5 text-zinc-400" />
                        {r.name}
                        {r.desfiemer_code && (
                          <span className="rounded-sm bg-zinc-100 px-1.5 py-0.5 font-mono text-[11px] text-zinc-600">
                            {r.desfiemer_code}
                          </span>
                        )}
                      </span>
                      {!r.initial?.product_id && !r.refresher?.product_id && (
                        <span className="mt-0.5 block text-[11px] text-amber-700">
                          Sin tarifa en el catálogo: al poner un precio se crea.
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <CellInput
                        value={r.hours_initial}
                        suffix="h"
                        testid={`prices-hours-initial-${r.region_id}`}
                        onSave={(v) =>
                          saveRegion(r, { hours_initial: v }, `hi-${r.region_id}`)
                        }
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <CellInput
                        value={r.initial?.unit_price}
                        suffix="€"
                        width="w-24"
                        testid={`prices-price-initial-${r.region_id}`}
                        onSave={(v) =>
                          saveRegion(r, { price_initial: v }, `pi-${r.region_id}`)
                        }
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <CellInput
                        value={r.hours_refresher}
                        suffix="h"
                        testid={`prices-hours-refresher-${r.region_id}`}
                        onSave={(v) =>
                          saveRegion(r, { hours_refresher: v }, `hr-${r.region_id}`)
                        }
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <CellInput
                        value={r.refresher?.unit_price}
                        suffix="€"
                        width="w-24"
                        testid={`prices-price-refresher-${r.region_id}`}
                        onSave={(v) =>
                          saveRegion(r, { price_refresher: v }, `pr-${r.region_id}`)
                        }
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <CellInput
                        value={r.max_students}
                        testid={`prices-max-students-${r.region_id}`}
                        width="w-16"
                        onSave={(v) =>
                          saveRegion(r, { max_students: v }, `ms-${r.region_id}`)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8">
            <h3 className="flex items-center gap-2 text-base font-bold text-zinc-950">
              <Route className="h-4 w-4" />
              Desplazamientos
            </h3>
            <p className="mt-1 text-sm text-zinc-600">
              Precio por trayecto según los kilómetros de ida. En la factura se
              cobra una unidad por cada día de curso.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {(data?.trips || []).map((t) => (
                <div
                  key={t.label}
                  className="rounded-sm border-2 border-zinc-200 bg-white p-3"
                  data-testid={`prices-trip-${(t.code || t.label).replace(/[^\w]+/g, "-")}`}
                >
                  <p className="text-xs font-bold uppercase tracking-wide text-zinc-500">
                    {t.label.replace("DESPLAZAMIENTO ", "")}
                  </p>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <CellInput
                      value={t.unit_price}
                      suffix="€"
                      width="w-24"
                      testid={`prices-trip-input-${(t.code || t.label).replace(/[^\w]+/g, "-")}`}
                      onSave={(v) => saveTrip(t, v ?? 0)}
                    />
                    {savingKey === t.product_id && (
                      <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
                    )}
                  </div>
                  <p className="mt-1 text-[11px] text-zinc-500">
                    Ahora: {eur(t.unit_price)}
                    {!t.product_id && " · falta en el catálogo"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="prices-region-dialog">
          <DialogHeader>
            <DialogTitle>Nueva comunidad</DialogTitle>
            <DialogDescription>
              Después podrás poner sus horas y precios en la tabla.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-zinc-700">
                Nombre *
              </label>
              <Input
                value={newRegion.name}
                onChange={(e) =>
                  setNewRegion({ ...newRegion, name: e.target.value.toUpperCase() })
                }
                placeholder="GALICIA"
                className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="prices-region-name"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-zinc-700">
                Código DF MR (2 letras)
              </label>
              <Input
                value={newRegion.desfiemer_code}
                onChange={(e) =>
                  setNewRegion({
                    ...newRegion,
                    desfiemer_code: e.target.value.toUpperCase(),
                  })
                }
                placeholder="GA"
                maxLength={3}
                className="h-11 w-28 border-2 border-zinc-300 font-mono focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="prices-region-code"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={createRegion}
              disabled={creating}
              className="bg-zinc-950 hover:bg-zinc-800"
              data-testid="prices-region-save"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Añadir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
